import React, { useState, useEffect, useCallback } from "react";
import { categoryAPI, leadPricingAPI, uploadAPI } from "../../services/api";
import { TableSkeleton } from "../../components/skeletons";

const GREEN = "#1B5C3A",
  GOLD = "#F5A623",
  PURPLE = "#4C1D95",
  CHAR = "#1a1a1a",
  MUTED = "#6b7280",
  BORDER = "#e5e7eb";

export const isImageUrl = (str) => {
  if (!str || typeof str !== "string") return false;
  const s = str.trim().toLowerCase();
  return (
    s.startsWith("http://") ||
    s.startsWith("https://") ||
    s.startsWith("/uploads/") ||
    s.startsWith("data:image/") ||
    /\.(png|jpe?g|webp|svg|gif|ico)($|\?)/i.test(s)
  );
};

export const BRAND_LOGOS = {
  'Apple': 'https://cdn.simpleicons.org/apple/000000',
  'Samsung': 'https://cdn.simpleicons.org/samsung/1428A0',
  'OnePlus': 'https://cdn.simpleicons.org/oneplus/F00000',
  'Xiaomi': 'https://cdn.simpleicons.org/xiaomi/FF6900',
  'Vivo': 'https://cdn.simpleicons.org/vivo/008CD6',
  'Realme': 'https://cdn.simpleicons.org/realme/FFC400',
  'Motorola': 'https://cdn.simpleicons.org/motorola/5C92FA',
  'Google': 'https://cdn.simpleicons.org/google/4285F4',
  'Nothing': 'https://cdn.simpleicons.org/nothing/000000',
  'iQOO': 'https://www.google.com/s2/favicons?domain=iqoo.com&sz=128',
  'Dell': 'https://cdn.simpleicons.org/dell/0076CE',
  'HP': 'https://cdn.simpleicons.org/hp/0096D6',
  'Lenovo': 'https://cdn.simpleicons.org/lenovo/E2231A',
  'Asus': 'https://cdn.simpleicons.org/asus/00539B',
  'Acer': 'https://cdn.simpleicons.org/acer/83B81A',
  'MSI': 'https://cdn.simpleicons.org/msi/FF0000',
  'boAt': 'https://www.google.com/s2/favicons?domain=boat-lifestyle.com&sz=128',
  'Sony': 'https://cdn.simpleicons.org/sony/000000',
  'JBL': 'https://cdn.simpleicons.org/jbl/FF6600',
  'Bose': 'https://cdn.simpleicons.org/bose/000000',
  'Sennheiser': 'https://cdn.simpleicons.org/sennheiser/000000',
  'Microsoft': 'https://cdn.simpleicons.org/microsoft/5E5E5E',
  'Nintendo': 'https://cdn.simpleicons.org/nintendo/E60012',
  'TCL': 'https://cdn.simpleicons.org/tcl/E20613',
  'LG': 'https://cdn.simpleicons.org/lg/A50034',
  'GoPro': 'https://cdn.simpleicons.org/gopro/00AEEF',
  'Canon': 'https://cdn.simpleicons.org/canon/CC0000',
  'Nikon': 'https://cdn.simpleicons.org/nikon/FFE500',
  'Noise': 'https://www.google.com/s2/favicons?domain=gonoise.com&sz=128',
  'Fitbit': 'https://cdn.simpleicons.org/fitbit/00B0B9',
  'Nike': 'https://cdn.simpleicons.org/nike/000000',
  'Adidas': 'https://cdn.simpleicons.org/adidas/000000',
  'Puma': 'https://cdn.simpleicons.org/puma/000000',
  'Bata': 'https://www.google.com/s2/favicons?domain=bata.com&sz=128',
  'Woodland': 'https://www.google.com/s2/favicons?domain=woodlandworldwide.com&sz=128',
  'Skechers': 'https://www.google.com/s2/favicons?domain=skechers.in&sz=128',
  'Reebok': 'https://cdn.simpleicons.org/reebok/000000',
  'Zara': 'https://cdn.simpleicons.org/zara/000000',
  'H&M': 'https://cdn.simpleicons.org/handm/E30613',
  'Levis': 'https://cdn.simpleicons.org/levis/E31B23',
  'Casio': 'https://cdn.simpleicons.org/casio/000000',
  'Titan': 'https://www.google.com/s2/favicons?domain=titan.co.in&sz=128',
  'Honda': 'https://cdn.simpleicons.org/honda/E60012',
  'Yamaha': 'https://cdn.simpleicons.org/yamaha/E60012',
  'Suzuki': 'https://cdn.simpleicons.org/suzuki/E20613',
  'KTM': 'https://cdn.simpleicons.org/ktm/FF6600',
  'Hyundai': 'https://cdn.simpleicons.org/hyundai/002C6C',
  'Tata': 'https://cdn.simpleicons.org/tata/0054A6',
  'Toyota': 'https://cdn.simpleicons.org/toyota/EB0A1E',
  'BYD': 'https://cdn.simpleicons.org/byd/000000',
  'IKEA': 'https://cdn.simpleicons.org/ikea/0051BA',
  'Bosch': 'https://cdn.simpleicons.org/bosch/E20015',
};

export const getAutoLogoUrl = (name) => {
  if (!name || typeof name !== "string" || !name.trim()) return "";
  const trimName = name.trim();
  if (BRAND_LOGOS[trimName]) return BRAND_LOGOS[trimName];
  const cleanDomain = trimName.toLowerCase().replace(/[^a-z0-9]/g, '') + '.com';
  return `https://www.google.com/s2/favicons?domain=${cleanDomain}&sz=128`;
};

export const renderIcon = (icon, imgStyle = {}, textStyle = {}) => {
  if (!icon) return null;
  if (isImageUrl(icon)) {
    return (
      <img
        src={icon}
        alt="logo"
        style={{
          width: 22,
          height: 22,
          objectFit: "contain",
          borderRadius: 4,
          marginRight: 6,
          verticalAlign: "middle",
          display: "inline-block",
          background: "#fff",
          border: `1px solid ${BORDER}`,
          ...imgStyle,
        }}
        onError={(e) => {
          e.target.style.display = "none";
        }}
      />
    );
  }
  return <span style={{ marginRight: 6, ...textStyle }}>{icon}</span>;
};

function Column({
  title,
  items,
  selected,
  onSelect,
  onAdd,
  onEdit,
  onDelete,
  onToggleActive,
  color,
  existingBrands,
  onShowNotice,
}) {
  const isBrandColumn = title === "Brands";
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newIcon, setNewIcon] = useState("");
  const [uploadingNew, setUploadingNew] = useState(false);

  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editIcon, setEditIcon] = useState("");
  const [uploadingEdit, setUploadingEdit] = useState(false);

  const handleFileUpload = async (e, setIconFn, setUploadingFn) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFn(true);
    try {
      const res = await uploadAPI.upload([file]);
      if (res.data?.data?.[0]?.url) {
        setIconFn(res.data.data[0].url);
      } else {
        if (onShowNotice) onShowNotice("Upload Error", "Failed to get uploaded image URL.", "error");
      }
    } catch (err) {
      if (onShowNotice) onShowNotice("Upload Error", err.response?.data?.message || "Image upload failed.", "error");
    } finally {
      setUploadingFn(false);
    }
  };

  const handleAdd = () => {
    if (!newName.trim()) {
      if (onShowNotice) {
        onShowNotice("Name Required", `Please enter a name for the new ${title ? title.slice(0, -1).toLowerCase() : "item"}.`, "warning");
      }
      return;
    }
    onAdd(newName.trim(), newIcon);
    setNewName("");
    setNewIcon("");
    setAdding(false);
  };
  const handleEdit = (id) => {
    if (!editName.trim()) {
      if (onShowNotice) {
        onShowNotice("Name Required", "Name cannot be empty.", "warning");
      }
      return;
    }
    onEdit(id, editName.trim(), editIcon);
    setEditId(null);
  };

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <h3
          style={{
            fontFamily: "var(--font-serif)",
            fontSize: 15,
            fontWeight: 700,
            color: CHAR,
            margin: 0,
          }}
        >
          {title}
        </h3>
        <button
          onClick={() => setAdding(!adding)}
          style={{
            background: color,
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "4px 12px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          + Add
        </button>
      </div>
      {adding && (
        <div
          style={{
            marginBottom: 10,
            background: "#f9fafb",
            borderRadius: 8,
            padding: 10,
            border: `1px solid ${BORDER}`,
          }}
        >
          {existingBrands?.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div
                style={{
                  fontSize: 10,
                  color: MUTED,
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                Quick pick from existing:
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {existingBrands.map((b) => (
                  <button
                    key={b.name + b.icon}
                    onClick={() => {
                      setNewName(b.name);
                      setNewIcon(b.icon || "");
                    }}
                    style={{
                      background: newName === b.name ? "#dcfce7" : "#fff",
                      border: `1px solid ${newName === b.name ? GREEN : BORDER}`,
                      borderRadius: 6,
                      padding: "3px 8px",
                      fontSize: 11,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                    }}
                  >
                    {renderIcon(b.icon, { width: 14, height: 14, marginRight: 2 })}
                    {b.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {renderIcon(newIcon, { width: 28, height: 28 })}
              <input
                className="form-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Name..."
                style={{ flex: 1, fontSize: 13, padding: "6px 10px" }}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                autoFocus
              />
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input
                value={newIcon}
                onChange={(e) => setNewIcon(e.target.value)}
                placeholder="Emoji or Logo Image URL..."
                style={{
                  flex: 1,
                  padding: "6px 8px",
                  borderRadius: 6,
                  border: `1px solid ${BORDER}`,
                  fontSize: 12,
                }}
              />
              {isBrandColumn && (
                <button
                  type="button"
                  onClick={() => {
                    if (!newName.trim()) {
                      if (onShowNotice) {
                        onShowNotice("Name Required", "Please enter the brand name first to auto-fetch its logo.", "warning");
                      }
                      return;
                    }
                    const autoUrl = getAutoLogoUrl(newName.trim());
                    setNewIcon(autoUrl);
                  }}
                  title="Auto-fetch official logo by brand name"
                  style={{
                    background: "#f0fdf4",
                    color: GREEN,
                    border: "1px solid #bbf7d0",
                    borderRadius: 6,
                    padding: "6px 10px",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                  }}
                >
                  ⚡ Auto Logo
                </button>
              )}
              <label
                style={{
                  background: "#eff6ff",
                  color: "#1d4ed8",
                  border: "1px solid #bfdbfe",
                  borderRadius: 6,
                  padding: "6px 10px",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: uploadingNew ? "wait" : "pointer",
                  whiteSpace: "nowrap",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {uploadingNew ? "Uploading..." : "📷 Upload"}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  disabled={uploadingNew}
                  onChange={(e) => handleFileUpload(e, setNewIcon, setUploadingNew)}
                />
              </label>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={handleAdd}
              style={{
                background: color,
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "6px 12px",
                fontSize: 12,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Save
            </button>
            <button
              onClick={() => {
                setAdding(false);
                setNewName("");
                setNewIcon("");
              }}
              style={{
                background: "#f3f4f6",
                color: MUTED,
                border: "none",
                borderRadius: 6,
                padding: "6px 12px",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
          <div style={{ fontSize: 10, color: MUTED, marginTop: 4 }}>
            Upload a brand logo image or paste an emoji / image URL
          </div>
        </div>
      )}
      <div
        style={{
          background: "#fff",
          border: `1px solid ${BORDER}`,
          borderRadius: 10,
          overflow: "hidden",
          maxHeight: 420,
          overflowY: "auto",
        }}
      >
        {items.length === 0 ? (
          <div
            style={{
              padding: "32px 16px",
              textAlign: "center",
              color: MUTED,
              fontSize: 13,
            }}
          >
            No items yet
          </div>
        ) : (
          items.map((item) => (
            <div
              key={item._id}
              onClick={() => onSelect(item)}
              style={{
                padding: "10px 14px",
                cursor: "pointer",
                borderBottom: `1px solid ${BORDER}`,
                background: selected?._id === item._id ? "#dcfce7" : "#fff",
                borderLeft:
                  selected?._id === item._id
                    ? `3px solid ${GREEN}`
                    : "3px solid transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                transition: "background 0.15s",
              }}
            >
              {editId === item._id ? (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, padding: "4px 0" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    {renderIcon(editIcon, { width: 24, height: 24 })}
                    <input
                      className="form-input"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      style={{ flex: 1, fontSize: 12, padding: "4px 8px" }}
                      onKeyDown={(e) => e.key === "Enter" && handleEdit(item._id)}
                      autoFocus
                    />
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <input
                      value={editIcon}
                      onChange={(e) => setEditIcon(e.target.value)}
                      placeholder="Emoji or Logo Image URL"
                      style={{
                        flex: 1,
                        padding: "4px 6px",
                        borderRadius: 4,
                        border: `1px solid ${BORDER}`,
                        fontSize: 11,
                      }}
                    />
                    {isBrandColumn && (
                      <button
                        type="button"
                        onClick={() => {
                          if (!editName.trim()) return;
                          const autoUrl = getAutoLogoUrl(editName.trim());
                          setEditIcon(autoUrl);
                        }}
                        title="Auto-fetch official logo by brand name"
                        style={{
                          background: "#f0fdf4",
                          color: GREEN,
                          border: "1px solid #bbf7d0",
                          borderRadius: 4,
                          padding: "4px 8px",
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        ⚡ Auto
                      </button>
                    )}
                    <label
                      style={{
                        background: "#eff6ff",
                        color: "#1d4ed8",
                        border: "1px solid #bfdbfe",
                        borderRadius: 4,
                        padding: "4px 8px",
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: uploadingEdit ? "wait" : "pointer",
                        whiteSpace: "nowrap",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      {uploadingEdit ? "..." : "📷 Logo"}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        disabled={uploadingEdit}
                        onChange={(e) => handleFileUpload(e, setEditIcon, setUploadingEdit)}
                      />
                    </label>
                    <button
                      onClick={() => handleEdit(item._id)}
                      style={{
                        background: GREEN,
                        color: "#fff",
                        border: "none",
                        borderRadius: 4,
                        padding: "4px 8px",
                        fontSize: 11,
                        cursor: "pointer",
                      }}
                    >
                      OK
                    </button>
                    <button
                      onClick={() => setEditId(null)}
                      style={{
                        background: "#f3f4f6",
                        color: MUTED,
                        border: "none",
                        borderRadius: 4,
                        padding: "4px 8px",
                        fontSize: 11,
                        cursor: "pointer",
                      }}
                    >
                      X
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: selected?._id === item._id ? 700 : 500,
                      color: item.isActive ? CHAR : MUTED,
                      textDecoration: item.isActive ? "none" : "line-through",
                      opacity: item.isActive ? 1 : 0.6,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {renderIcon(item.icon, { width: 22, height: 22 })}
                    {item.name}
                  </span>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div
                        onClick={() => onToggleActive(item._id, !item.isActive, item.name)}
                        style={{
                          position: "relative",
                          display: "inline-block",
                          width: "36px",
                          height: "20px",
                          backgroundColor: item.isActive ? GREEN : "#D1D5DB",
                          borderRadius: "10px",
                          cursor: "pointer",
                          transition: "background-color 0.2s ease",
                        }}
                        title={item.isActive ? "Disable" : "Enable"}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: "2px",
                            left: item.isActive ? "18px" : "2px",
                            width: "16px",
                            height: "16px",
                            backgroundColor: "#FFFFFF",
                            borderRadius: "50%",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                            transition: "left 0.2s ease",
                          }}
                        />
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, color: item.isActive ? GREEN : MUTED, width: 22 }}>
                        {item.isActive ? "ON" : "OFF"}
                      </span>
                    </div>
                    {isBrandColumn && (
                      <button
                        onClick={() => {
                          const autoUrl = getAutoLogoUrl(item.name);
                          if (autoUrl) {
                            onEdit(item._id, item.name, autoUrl);
                          }
                        }}
                        title="Auto-fetch official brand logo"
                        style={{
                          background: "#f0fdf4",
                          color: GREEN,
                          border: "1px solid #bbf7d0",
                          borderRadius: 4,
                          fontSize: 10,
                          padding: "2px 6px",
                          cursor: "pointer",
                          fontWeight: 700,
                        }}
                      >
                        ⚡ Logo
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setEditId(item._id);
                        setEditName(item.name);
                        setEditIcon(item.icon || "");
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: 12,
                        padding: "2px 4px",
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => onDelete(item._id, item.name)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: 12,
                        padding: "2px 4px",
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function LeadCostPanel({ selectedCat, selectedSub, selectedBrand, onSelectRule, onShowNotice }) {
  const [pricing, setPricing] = useState(null);
  const [form, setForm] = useState({
    localRate: "",
    outCityRate: "",
    bulkRate: "",
    leadReceivingFrom: "home_state",
  });
  const [saving, setSaving] = useState(false);
  const [pricingId, setPricingId] = useState(null);

  const catId = selectedCat?._id;
  const subId = selectedSub?._id || null;
  const brandId = selectedBrand?._id || null;

  // Build label for what we're pricing
  const label = [selectedCat?.name, selectedSub?.name, selectedBrand?.name]
    .filter(Boolean)
    .join(" › ");
  const level = selectedBrand
    ? "Brand"
    : selectedSub
      ? "Subcategory"
      : selectedCat
        ? "Category"
        : null;

  const [allRulesForCat, setAllRulesForCat] = useState([]);

  useEffect(() => {
    if (!catId) {
      setPricing(null);
      setAllRulesForCat([]);
      return;
    }
    leadPricingAPI
      .list()
      .then((r) => {
        const rules = r.data.data || [];
        // All rules under this category
        const catRules = rules.filter(
          (r) => String(r.categoryId?._id || r.categoryId) === String(catId),
        );
        setAllRulesForCat(catRules);

        // Exact match for the selected node
        const match = rules.find((r) => {
          const rCat = r.categoryId?._id || r.categoryId;
          const rSub = r.subCategoryId?._id || r.subCategoryId;
          const rBrand = r.brandId?._id || r.brandId;
          return (
            String(rCat) === String(catId) &&
            String(rSub || "") === String(subId || "") &&
            String(rBrand || "") === String(brandId || "")
          );
        });
        if (match) {
          setPricing(match);
          setPricingId(match._id);
          setForm({
            localRate: match.localRate || "",
            outCityRate: match.outCityRate || "",
            bulkRate: match.bulkRate || "",
            leadReceivingFrom: match.leadReceivingFrom || "home_state",
          });
        } else {
          setPricing(null);
          setPricingId(null);
          setForm({ localRate: "", outCityRate: "", bulkRate: "", leadReceivingFrom: "home_state" });
        }
      })
      .catch(() => {});
  }, [catId, subId, brandId]);

  const handleSave = async () => {
    if (!form.localRate) {
      if (onShowNotice) {
        onShowNotice("Lead Rate Required", "Please enter the Lead Rate (₹) before saving.", "warning");
      }
      return;
    }
    setSaving(true);
    try {
      const scope = form.leadReceivingFrom || "home_state";
      const body = {
        categoryId: catId,
        subCategoryId: subId,
        brandId: brandId,
        leadReceivingFrom: scope,
        localRate: parseFloat(form.localRate) || 0,
        outCityRate: scope === "home_state"
          ? (form.outCityRate ? parseFloat(form.outCityRate) : Number((parseFloat(form.localRate) * 0.5).toFixed(2)))
          : null,
        bulkRate: form.bulkRate ? parseFloat(form.bulkRate) : null,
      };
      if (pricingId) {
        await leadPricingAPI.update(pricingId, body);
      } else {
        const res = await leadPricingAPI.create(body);
        if (res.data?.data?._id) setPricingId(res.data.data._id);
      }
      setPricing(body);
      // Instantly refresh list of existing rules under this category
      leadPricingAPI.list().then((r) => {
        const rules = r.data.data || [];
        const catRules = rules.filter(
          (rule) => String(rule.categoryId?._id || rule.categoryId) === String(catId),
        );
        setAllRulesForCat(catRules);
      }).catch(() => {});
      if (onShowNotice) {
        onShowNotice("Lead Cost Saved!", `Successfully saved lead cost rates for ${label}.`, "success");
      }
    } catch (err) {
      if (onShowNotice) {
        onShowNotice("Save Failed", err.response?.data?.message || "Failed to save lead cost.", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!catId)
    return (
      <div
        style={{
          background: "#fff",
          border: `1px solid ${BORDER}`,
          borderRadius: 12,
          padding: 24,
          textAlign: "center",
          color: MUTED,
        }}
      >
        <div style={{ fontSize: 28, marginBottom: 8 }}>💲</div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>
          Lead Cost Configuration
        </div>
        <div style={{ fontSize: 12, marginTop: 4 }}>
          Select a category, subcategory, or brand to set lead costs
        </div>
      </div>
    );

  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${BORDER}`,
        borderRadius: 12,
        padding: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 14,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: MUTED,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Lead Cost — {level} Level
          </div>
          <div
            style={{ fontSize: 15, fontWeight: 700, color: CHAR, marginTop: 2 }}
          >
            {renderIcon(selectedCat?.icon)}
            {renderIcon(selectedSub?.icon)}
            {renderIcon(selectedBrand?.icon)}
            {label}
          </div>
        </div>
        {pricing && (
          <span
            style={{
              background: "#dcfce7",
              color: GREEN,
              padding: "3px 10px",
              borderRadius: 10,
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            ✓ Configured
          </span>
        )}
      </div>

      <div style={{ marginBottom: 14 }}>
        <label
          style={{
            display: "block",
            fontSize: 11,
            fontWeight: 600,
            color: MUTED,
            marginBottom: 4,
          }}
        >
          Lead Receiving From *
        </label>
        <select
          value={form.leadReceivingFrom || "home_state"}
          onChange={(e) => {
            const val = e.target.value;
            setForm({ ...form, leadReceivingFrom: val });
          }}
          className="form-input"
          style={{ width: "100%", fontSize: 13, height: 38, padding: "6px 10px", borderRadius: 8, border: `1px solid ${BORDER}` }}
        >
          <option value="home_city">Home City Only</option>
          <option value="home_state">Home State (All Cities)</option>
          <option value="nation_wide">Nation Wide (All Cities of All States)</option>
        </select>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: (form.leadReceivingFrom === "home_city" || form.leadReceivingFrom === "nation_wide") ? "1fr 1fr" : "1fr 1fr 1fr",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div>
          <label
            style={{
              display: "block",
              fontSize: 11,
              fontWeight: 600,
              color: MUTED,
              marginBottom: 4,
            }}
          >
            {(form.leadReceivingFrom === "home_city" || form.leadReceivingFrom === "nation_wide") ? "Lead Rate (₹) *" : "Local Rate (₹) *"}
          </label>
          <input
            className="form-input"
            type="number"
            min="0"
            value={form.localRate}
            onChange={(e) => {
              const val = e.target.value;
              setForm({
                ...form,
                localRate: val,
                outCityRate: val ? String(Number((parseFloat(val) * 0.5).toFixed(2))) : "",
              });
            }}
            onWheel={(e) => e.target.blur()} 
            placeholder="e.g. 30"
            style={{
              fontSize: 16,
              fontWeight: 700,
              fontFamily: "var(--font-serif)",
            }}
          />
          <div style={{ fontSize: 10, color: MUTED, marginTop: 2 }}>
            {(form.leadReceivingFrom === "home_city" || form.leadReceivingFrom === "nation_wide") ? "Flat rate for matching leads" : "Full rate for same-city vendors"}
          </div>
        </div>
        
        {!(form.leadReceivingFrom === "home_city" || form.leadReceivingFrom === "nation_wide") && (
          <div>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 600,
                color: MUTED,
                marginBottom: 4,
              }}
            >
              Out-City Rate (₹)
            </label>
            <input
              className="form-input"
              type="number"
              min="0"
              value={form.outCityRate}
              onChange={(e) => setForm({ ...form, outCityRate: e.target.value })}
              onWheel={(e) => e.target.blur()} 
              placeholder="Auto: 50%"
              style={{
                fontSize: 16,
                fontWeight: 700,
                fontFamily: "var(--font-serif)",
              }}
            />
            <div style={{ fontSize: 10, color: MUTED, marginTop: 2 }}>
              Defaults to 50% of local
            </div>
          </div>
        )}

        <div>
          <label
            style={{
              display: "block",
              fontSize: 11,
              fontWeight: 600,
              color: MUTED,
              marginBottom: 4,
            }}
          >
            Bulk Rate (₹)
          </label>
          <input
            className="form-input"
            type="number"
            min="0"
            value={form.bulkRate}
            onChange={(e) => setForm({ ...form, bulkRate: e.target.value })}
            onWheel={(e) => e.target.blur()} 
            placeholder="Optional"
            style={{
              fontSize: 16,
              fontWeight: 700,
              fontFamily: "var(--font-serif)",
            }}
          />
          <div style={{ fontSize: 10, color: MUTED, marginTop: 2 }}>
            For bulk sale leads
          </div>
        </div>
      </div>

      <div
        style={{
          background: "#f0fdf4",
          border: "1px solid #bbf7d0",
          borderRadius: 8,
          padding: "8px 12px",
          marginBottom: 14,
          fontSize: 11,
          color: GREEN,
        }}
      >
        💡 Most specific pricing wins: Brand overrides Subcategory, Subcategory
        overrides Category.
        {form.localRate && (
          <span style={{ display: "block", marginTop: 4, fontWeight: 700 }}>
            {(form.leadReceivingFrom === "home_city" || form.leadReceivingFrom === "nation_wide") ? (
              `Rate: ₹${form.localRate}`
            ) : (
              `Local: ₹${form.localRate} · Out-city: ₹${form.outCityRate || Number((parseFloat(form.localRate) * 0.5).toFixed(2))} (50%)`
            )}
            {form.bulkRate ? ` · Bulk: ₹${form.bulkRate}` : ""}
          </span>
        )}
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          background: GREEN,
          color: "#fff",
          border: "none",
          borderRadius: 9,
          padding: "10px 24px",
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
          width: "100%",
        }}
      >
        {saving
          ? "Saving..."
          : pricingId
            ? "Update Lead Cost"
            : "Set Lead Cost"}
      </button>

      {/* Existing pricing rules for this category */}
      {allRulesForCat.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: MUTED,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 8,
            }}
          >
            Existing Pricing Under {renderIcon(selectedCat?.icon)} {selectedCat?.name} (
            {allRulesForCat.length} rules)
          </div>
          <div
            style={{
              maxHeight: 200,
              overflowY: "auto",
              border: `1px solid ${BORDER}`,
              borderRadius: 8,
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
              }}
            >
              <thead>
                <tr
                  style={{ background: "#f9fafb", position: "sticky", top: 0 }}
                >
                  <th
                    style={{
                      padding: "6px 10px",
                      textAlign: "left",
                      fontWeight: 600,
                      color: MUTED,
                      fontSize: 10,
                      textTransform: "uppercase",
                    }}
                  >
                    Sub-Category
                  </th>
                  <th
                    style={{
                      padding: "6px 10px",
                      textAlign: "left",
                      fontWeight: 600,
                      color: MUTED,
                      fontSize: 10,
                      textTransform: "uppercase",
                    }}
                  >
                    Brand
                  </th>
                  <th
                    style={{
                      padding: "6px 10px",
                      textAlign: "right",
                      fontWeight: 600,
                      color: MUTED,
                      fontSize: 10,
                      textTransform: "uppercase",
                    }}
                  >
                    Local
                  </th>
                  <th
                    style={{
                      padding: "6px 10px",
                      textAlign: "right",
                      fontWeight: 600,
                      color: MUTED,
                      fontSize: 10,
                      textTransform: "uppercase",
                    }}
                  >
                    Out-City
                  </th>
                  <th
                    style={{
                      padding: "6px 10px",
                      textAlign: "right",
                      fontWeight: 600,
                      color: MUTED,
                      fontSize: 10,
                      textTransform: "uppercase",
                    }}
                  >
                    Bulk
                  </th>
                </tr>
              </thead>
              <tbody>
                {allRulesForCat.map((rule) => {
                  const isSelected = rule._id === pricingId;
                  return (
                    <tr
                      key={rule._id}
                      style={{
                        borderBottom: `1px solid ${BORDER}`,
                        background: isSelected ? "#dcfce7" : "#fff",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        // Click to load this rule into the form and select its node in columns
                        setPricing(rule);
                        setPricingId(rule._id);
                        setForm({
                          localRate: rule.localRate || "",
                          outCityRate: rule.outCityRate || "",
                          bulkRate: rule.bulkRate || "",
                          leadReceivingFrom: rule.leadReceivingFrom || "home_state",
                        });
                        if (onSelectRule) {
                          onSelectRule(rule.categoryId, rule.subCategoryId, rule.brandId);
                        }
                      }}
                    >
                      <td style={{ padding: "6px 10px", fontWeight: 500 }}>
                        {renderIcon(rule.subCategoryId?.icon)}
                        {rule.subCategoryId?.name || "—"}
                      </td>
                      <td style={{ padding: "6px 10px" }}>
                        {renderIcon(rule.brandId?.icon)}
                        {rule.brandId?.name || "—"}
                      </td>
                      <td
                        style={{
                          padding: "6px 10px",
                          textAlign: "right",
                          fontWeight: 700,
                          color: GREEN,
                        }}
                      >
                        ₹{rule.localRate}
                      </td>
                      <td
                        style={{
                          padding: "6px 10px",
                          textAlign: "right",
                          fontWeight: 700,
                          color: GOLD,
                        }}
                      >
                        ₹{rule.outCityRate}
                      </td>
                      <td
                        style={{
                          padding: "6px 10px",
                          textAlign: "right",
                          color: MUTED,
                        }}
                      >
                        ₹{rule.bulkRate || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TaxonomyManager() {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allBrands, setAllBrands] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null);
  const [noticeModal, setNoticeModal] = useState(null);
  const [syncingLogos, setSyncingLogos] = useState(false);

  const showNotice = (title, message, type = "warning") => {
    setNoticeModal({ title, message, type });
  };

  const loadCategories = useCallback(() => {
    categoryAPI
      .tree({ admin: true })
      .then((r) => {
        setCategories((r.data.data || []).filter((c) => !c.parent));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadCategories();
    // Load all unique brands for quick-pick
    categoryAPI
      .flat({ admin: true })
      .then((r) => {
        const all = r.data.data || [];
        const brandItems = all.filter((c) => c.type === "brand");
        const unique = [];
        const seen = new Set();
        brandItems.forEach((b) => {
          if (!seen.has(b.name)) {
            seen.add(b.name);
            unique.push({ name: b.name, icon: b.icon });
          }
        });
        setAllBrands(unique.sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => {});
  }, [loadCategories]);

  const loadSubcategories = useCallback((catId) => {
    categoryAPI
      .children(catId, { admin: true })
      .then((r) => setSubcategories(r.data.data || []))
      .catch(() => setSubcategories([]));
  }, []);
  const loadBrands = useCallback((subId) => {
    categoryAPI
      .children(subId, { admin: true })
      .then((r) => setBrands(r.data.data || []))
      .catch(() => setBrands([]));
  }, []);

  const selectCategory = (cat) => {
    setSelectedCat(cat);
    setSelectedSub(null);
    setSelectedBrand(null);
    setBrands([]);
    loadSubcategories(cat._id);
  };
  const selectSubcategory = (sub) => {
    setSelectedSub(sub);
    setSelectedBrand(null);
    loadBrands(sub._id);
  };
  const selectBrand = (brand) => {
    setSelectedBrand(brand);
  };

  const handleSelectRuleFromTable = (ruleCat, ruleSub, ruleBrand) => {
    if (ruleCat) {
      const catObj = typeof ruleCat === "object" ? ruleCat : categories.find((c) => c._id === ruleCat) || { _id: ruleCat };
      setSelectedCat(catObj);
      loadSubcategories(catObj._id);

      if (ruleSub) {
        const subObj = typeof ruleSub === "object" ? ruleSub : { _id: ruleSub };
        setSelectedSub(subObj);
        loadBrands(subObj._id);
      } else {
        setSelectedSub(null);
        setBrands([]);
      }

      if (ruleBrand) {
        const brandObj = typeof ruleBrand === "object" ? ruleBrand : { _id: ruleBrand };
        setSelectedBrand(brandObj);
      } else {
        setSelectedBrand(null);
      }
    }
  };

  const addCategory = (name, icon) =>
    categoryAPI.createCategory({ name, icon }).then(() => loadCategories());
  const editCategory = (id, name, icon) =>
    categoryAPI.updateCategory(id, { name, icon }).then(() => loadCategories());

  const addSubcategory = (name, icon) => {
    if (!selectedCat) {
      return showNotice("Category Required", "Please select a Category from the first column before adding a subcategory.", "warning");
    }
    categoryAPI
      .createSubcategory({ name, icon, categories: [selectedCat._id] })
      .then(() => loadSubcategories(selectedCat._id))
      .catch((err) =>
        showNotice("Action Failed", err.response?.data?.message || "Failed to add subcategory.", "error"),
      );
  };
  const editSubcategory = (id, name, icon) => {
    categoryAPI
      .updateSubcategory(id, { name, icon })
      .then(() => {
        loadSubcategories(selectedCat._id);
        if (selectedSub?._id === id) {
          setSelectedSub({ ...selectedSub, name, icon });
        }
      })
      .catch((err) =>
        showNotice("Action Failed", err.response?.data?.message || "Failed to update subcategory.", "error"),
      );
  };

  const addBrand = (name, icon) => {
    if (!selectedSub) {
      return showNotice("Subcategory Required", "Please select a Subcategory from the second column before adding a brand.", "warning");
    }
    categoryAPI
      .createBrand({ name, icon, subCategoryId: selectedSub._id })
      .then(() => loadBrands(selectedSub._id))
      .catch((err) =>
        showNotice("Action Failed", err.response?.data?.message || "Failed to add brand.", "error"),
      );
  };
  const editBrand = (id, name, icon) => {
    categoryAPI
      .updateBrand(id, { name, icon })
      .then(() => {
        loadBrands(selectedSub._id);
        if (selectedBrand?._id === id) {
          setSelectedBrand({ ...selectedBrand, name, icon });
        }
      })
      .catch((err) =>
        showNotice("Action Failed", err.response?.data?.message || "Failed to update brand.", "error"),
      );
  };

  const deleteCategory = (id, name) => {
    setConfirmModal({ type: "delete", nodeType: "Category", id, name });
  };

  const deleteSubcategory = (id, name) => {
    setConfirmModal({ type: "delete", nodeType: "Subcategory", id, name });
  };

  const deleteBrand = (id, name) => {
    setConfirmModal({ type: "delete", nodeType: "Brand", id, name });
  };

  const toggleCategoryActive = (id, isActive, name) => {
    setConfirmModal({ type: "toggle", nodeType: "Category", id, name, targetValue: isActive });
  };

  const toggleSubcategoryActive = (id, isActive, name) => {
    setConfirmModal({ type: "toggle", nodeType: "Subcategory", id, name, targetValue: isActive });
  };

  const toggleBrandActive = (id, isActive, name) => {
    setConfirmModal({ type: "toggle", nodeType: "Brand", id, name, targetValue: isActive });
  };

  const handleConfirmAction = (modal) => {
    setConfirmModal(null);
    if (modal.type === "delete") {
      if (modal.nodeType === "Category") {
        categoryAPI
          .removeCategory(modal.id)
          .then(() => {
            loadCategories();
            setSelectedCat(null);
            setSubcategories([]);
            setBrands([]);
          })
          .catch((err) =>
            showNotice("Delete Failed", err.response?.data?.message || "Failed to delete category.", "error"),
          );
      } else if (modal.nodeType === "Subcategory") {
        categoryAPI
          .removeSubcategory(modal.id)
          .then(() => {
            loadSubcategories(selectedCat._id);
            if (selectedSub?._id === modal.id) {
              setSelectedSub(null);
              setBrands([]);
            }
          })
          .catch((err) =>
            showNotice("Delete Failed", err.response?.data?.message || "Failed to delete subcategory.", "error"),
          );
      } else if (modal.nodeType === "Brand") {
        categoryAPI
          .removeBrand(modal.id)
          .then(() => {
            loadBrands(selectedSub._id);
            if (selectedBrand?._id === modal.id) {
              setSelectedBrand(null);
            }
          })
          .catch((err) =>
            showNotice("Delete Failed", err.response?.data?.message || "Failed to delete brand.", "error"),
          );
      }
    } else if (modal.type === "toggle") {
      const isActive = modal.targetValue;
      if (modal.nodeType === "Category") {
        categoryAPI
          .updateCategory(modal.id, { isActive })
          .then(() => loadCategories())
          .catch((err) =>
            showNotice("Update Failed", err.response?.data?.message || "Failed to toggle status.", "error"),
          );
      } else if (modal.nodeType === "Subcategory") {
        categoryAPI
          .updateSubcategory(modal.id, { isActive })
          .then(() => loadSubcategories(selectedCat._id))
          .catch((err) =>
            showNotice("Update Failed", err.response?.data?.message || "Failed to toggle status.", "error"),
          );
      } else if (modal.nodeType === "Brand") {
        categoryAPI
          .updateBrand(modal.id, { isActive })
          .then(() => loadBrands(selectedSub._id))
          .catch((err) =>
            showNotice("Update Failed", err.response?.data?.message || "Failed to toggle status.", "error"),
          );
      }
    }
  };

  if (loading) return <TableSkeleton rows={8} columns={5} style={{ padding: 24 }} />;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1
          style={{
            fontFamily: "var(--font-serif)",
            fontSize: 26,
            fontWeight: 700,
            color: CHAR,
            margin: 0,
          }}
        >
          Taxonomy & Lead Pricing
        </h1>
        <p style={{ color: MUTED, fontSize: 13, marginTop: 4 }}>
          Manage categories, subcategories, brands — and set lead costs per node
        </p>
      </div>

      {/* 3-Column Taxonomy */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <Column
          title="Categories"
          items={categories}
          selected={selectedCat}
          onSelect={selectCategory}
          onAdd={addCategory}
          onEdit={editCategory}
          onDelete={deleteCategory}
          onToggleActive={toggleCategoryActive}
          color={GREEN}
          onShowNotice={showNotice}
        />
        <Column
          title="Subcategories"
          items={subcategories}
          selected={selectedSub}
          onSelect={selectSubcategory}
          onAdd={addSubcategory}
          onEdit={editSubcategory}
          onDelete={deleteSubcategory}
          onToggleActive={toggleSubcategoryActive}
          color={PURPLE}
          onShowNotice={showNotice}
        />
        <Column
          title="Brands"
          items={brands}
          selected={selectedBrand}
          onSelect={selectBrand}
          onAdd={addBrand}
          onEdit={editBrand}
          onDelete={deleteBrand}
          onToggleActive={toggleBrandActive}
          color={GOLD}
          existingBrands={allBrands}
          onShowNotice={showNotice}
        />
      </div>

      {/* Lead Cost Panel — below the 3 columns */}
      <LeadCostPanel
        selectedCat={selectedCat}
        selectedSub={selectedSub}
        selectedBrand={selectedBrand}
        onSelectRule={handleSelectRuleFromTable}
        onShowNotice={showNotice}
      />

      {/* Modern Confirmation Modal */}
      {confirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '380px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: confirmModal.type === 'delete' ? '#FEE2E2' : '#FEF3C7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              color: confirmModal.type === 'delete' ? '#DC2626' : '#D97706'
            }}>
              {confirmModal.type === 'delete' ? (
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              ) : (
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              )}
            </div>
            <h3 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '20px',
              fontWeight: 700,
              color: '#1A1A1A',
              marginBottom: '12px'
            }}>
              {confirmModal.type === 'delete' ? `Delete ${confirmModal.nodeType}?` : `Toggle Status?`}
            </h3>
            <p style={{
              fontSize: '14px',
              color: MUTED,
              lineHeight: '1.6',
              marginBottom: '24px'
            }}>
              {confirmModal.type === 'delete'
                ? `Are you sure you want to delete "${confirmModal.name}"? This action will hide this ${confirmModal.nodeType.toLowerCase()} from the ecosystem but preserve historical relations.`
                : `Are you sure you want to ${confirmModal.targetValue ? 'enable' : 'disable'} "${confirmModal.name}"? If you disable it, it will be hidden from both buyers and vendors.`}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setConfirmModal(null)}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  borderRadius: '9px',
                  border: `1.5px solid ${BORDER}`,
                  background: '#fff',
                  color: MUTED,
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
              >
                Cancel
              </button>
              <button
                onClick={() => handleConfirmAction(confirmModal)}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  borderRadius: '9px',
                  border: 'none',
                  background: confirmModal.type === 'delete' ? '#DC2626' : GREEN,
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = confirmModal.type === 'delete' ? '#B91C1C' : GREEN}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = confirmModal.type === 'delete' ? '#DC2626' : GREEN}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modern Custom Validation / Alert Dialogue Modal */}
      {noticeModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99999,
          }}
          onClick={() => setNoticeModal(null)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              padding: "24px",
              width: "90%",
              maxWidth: "380px",
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
              textAlign: "center",
              border: `1px solid ${BORDER}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                backgroundColor:
                  noticeModal.type === "error"
                    ? "#FEE2E2"
                    : noticeModal.type === "success"
                      ? "#DCFCE7"
                      : "#FEF3C7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                fontSize: 26,
              }}
            >
              {noticeModal.type === "error"
                ? "⚠️"
                : noticeModal.type === "success"
                  ? "✅"
                  : "💡"}
            </div>

            <h3
              style={{
                fontFamily: "var(--font-serif)",
                fontSize: 18,
                fontWeight: 700,
                color: CHAR,
                margin: "0 0 8px 0",
              }}
            >
              {noticeModal.title || "Notice"}
            </h3>

            <p
              style={{
                fontSize: 13,
                color: MUTED,
                lineHeight: 1.5,
                margin: "0 0 20px 0",
              }}
            >
              {noticeModal.message}
            </p>

            <button
              onClick={() => setNoticeModal(null)}
              style={{
                width: "100%",
                padding: "10px 16px",
                background:
                  noticeModal.type === "error"
                    ? "#DC2626"
                    : noticeModal.type === "success"
                      ? GREEN
                      : GREEN,
                color: "#ffffff",
                border: "none",
                borderRadius: "8px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
              autoFocus
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
